import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import socketService from '../services/socketService';
import { playMessageTone } from '../services/audioUtil';
import { useAuth } from './AuthContext';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [userCache, setUserCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const convsRef = useRef(conversations);
  const msgsRef = useRef(messages);
  const cacheRef = useRef(userCache);
  const myIdRef = useRef(currentUserId);

  useEffect(() => {
    myIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    convsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    msgsRef.current = messages;
  }, [messages]);

  useEffect(() => {
    cacheRef.current = userCache;
  }, [userCache]);

  useEffect(() => {
    if (user) setCurrentUserId(user._id);
  }, [user]);

  const notify = useCallback(() => {
    setConversations([...convsRef.current]);
  }, []);

  const _onMessage = useCallback((data) => {
    const convId = data.conversationId;
    if (data.senderId !== myIdRef.current) {
      playMessageTone();
    }
    const prev = msgsRef.current[convId] || [];
    const exists = prev.find((m) => m._id === data._id);
    if (exists) return;
    const updated = { ...msgsRef.current, [convId]: [...prev, data] };
    setMessages(updated);

    const convs = convsRef.current.map((c) => {
      if (c._id === convId) {
        return {
          ...c,
          lastMessage: {
            content: data.content || (data.type === 'image' ? '📷 Photo' : data.type === 'video' ? '📹 Video' : ''),
            type: data.type,
            senderId: data.senderId,
            createdAt: data.createdAt,
          },
          lastActivity: data.createdAt,
        };
      }
      return c;
    });
    convs.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
    setConversations(convs);
  }, []);

  const _onTyping = useCallback((data) => {
    const key = `${data.conversationId}:${data.userId}`;
    setTypingUsers((prev) => ({ ...prev, [key]: data.isTyping }));
  }, []);

  const _onUserOnline = useCallback((data) => {
    const cached = cacheRef.current[data.userId];
    if (cached) {
      setUserCache((prev) => ({ ...prev, [data.userId]: { ...cached, online: true } }));
    }
  }, []);

  const _onUserOffline = useCallback((data) => {
    const cached = cacheRef.current[data.userId];
    if (cached) {
      setUserCache((prev) => ({ ...prev, [data.userId]: { ...cached, online: false, lastSeen: data.lastSeen } }));
    }
  }, []);

  useEffect(() => {
    socketService.on('chat:message', _onMessage);
    socketService.on('chat:typing', _onTyping);
    socketService.on('user:online', _onUserOnline);
    socketService.on('user:offline', _onUserOffline);

    const unsub = socketService.onConnectionChange((connected) => {
      if (connected) {
        loadConversations();
      }
    });

    return () => {
      socketService.off('chat:message');
      socketService.off('chat:typing');
      socketService.off('user:online');
      socketService.off('user:offline');
      unsub();
    };
  }, []);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getConversations();
      data.sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
      setConversations(data);

      const ids = new Set();
      data.forEach((c) => c.participants.forEach((p) => ids.add(p)));
      ids.forEach((id) => {
        if (!cacheRef.current[id]) {
          apiService.getUser(id).then((u) => {
            setUserCache((prev) => ({ ...prev, [u._id]: u }));
          }).catch(() => {});
        }
      });
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const cacheUser = useCallback((userId) => {
    if (cacheRef.current[userId]) return;
    apiService.getUser(userId).then((u) => {
      setUserCache((prev) => ({ ...prev, [u._id]: u }));
    }).catch(() => {});
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    try {
      const data = await apiService.getMessages(conversationId);
      setMessages((prev) => ({ ...prev, [conversationId]: data }));
      return data;
    } catch (err) {
      console.error('Failed to load messages:', err);
      return [];
    }
  }, []);

  const sendMessage = useCallback((convId, content, type = 'text', mediaUrl = null) => {
    socketService.sendMessage(convId, content, type, mediaUrl);
  }, []);

  const markRead = useCallback((convId) => {
    socketService.markRead(convId);
  }, []);

  const sendTyping = useCallback((convId, isTyping) => {
    socketService.sendTyping(convId, isTyping);
  }, []);

  const refresh = useCallback(() => {
    loadConversations();
  }, [loadConversations]);

  const isTyping = useCallback(
    (convId, userId) => {
      return typingUsers[`${convId}:${userId}`] || false;
    },
    [typingUsers]
  );

  const getUser = useCallback(
    (id) => {
      return userCache[id] || null;
    },
    [userCache]
  );

  return (
    <ChatContext.Provider
      value={{
        conversations,
        messages,
        typingUsers,
        userCache,
        loading,
        currentUserId,
        loadConversations,
        cacheUser,
        loadMessages,
        sendMessage,
        markRead,
        sendTyping,
        refresh,
        isTyping,
        getUser,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
}
